import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { layout } from '@modernhealth/vital-foundation';
import { Grid, HeadingText50, DeprecatedInlineMessage } from '@modernhealth/vital-components';
import { captureException } from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { CircleRsvpFragment, useGetCircleRsvpsQuery } from '@modernhealth/circles-graphql/v2';
import { filterNullish } from '@modernhealth/filter-nullish';

import { BackButton } from '../components/BackButton';
import { SessionCardRowSkeleton } from '../components/SessionCardSkeletons';
import { PageSection } from '../components/PageSection';
import { FeaturedTopicsRow } from '../components/FeaturedTopicsRow';
import { CircleRsvpCard } from '../components/CircleRsvpCard';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
    display: 'flex',
    flexDirection: 'column',
    gap: 48,
});

const RsvpGrid: React.FC<{ circleRsvps: CircleRsvpFragment[]; isPrevious?: boolean }> = ({
    circleRsvps,
    isPrevious,
}) => {
    return (
        <Grid lgMode="3columns">
            {circleRsvps.map(rsvp => {
                return (
                    <CircleRsvpCard
                        key={rsvp.id}
                        rsvp={rsvp}
                        isPrevious={isPrevious}
                        id={`rsvp-card-preview-${rsvp.id}`}
                    />
                );
            })}
        </Grid>
    );
};

/**
 * "Recommendations" row as a fallback if the user has
 * no registered Circles, uses featured Circles for now.
 */
export const RecommendationsRow = () => {
    const { t } = useTranslation('circles');

    return (
        <>
            <DeprecatedInlineMessage
                heading={t('pages.your_circles.recommendations.heading')}
                message={t('pages.your_circles.recommendations.message')}
                alignment="center"
            />
            <FeaturedTopicsRow header="Recommended Circles" />
        </>
    );
};

// This date is roughly the date after which authenticated Circles
// were enabled in the backend. The API currently needs a "starting date"
// to query.
const START_OF_AUTHENTICATED_CIRCLES = '2022-05-15T00:00:00.000Z';

/**
 * Hook to handle/hide details of error handling for `circleRsvps` queries
 */
function useRsvpsQuery(variables: { first: number } | { last: number }) {
    const first = 'first' in variables ? variables.first : null;
    const last = 'last' in variables ? variables.last : null;
    const [nowISOString] = useState(new Date().toISOString());

    const memoizedVariables = useMemo(() => {
        return last
            ? {
                  last,
                  // circleRsvps requires `afterDateTime` with `last`
                  // for now...
                  afterDateTime: START_OF_AUTHENTICATED_CIRCLES,
                  beforeDateTime: nowISOString,
              }
            : {
                  afterDateTime: nowISOString,
                  first,
              };
    }, [first, last, nowISOString]);

    const { data, loading, error } = useGetCircleRsvpsQuery({
        variables: memoizedVariables,
    });

    useEffect(() => {
        if (error) captureException(error);
    }, [error]);

    if (loading) return { loading, data: null };

    if (!data?.circleRsvps) return { loading: false, data: null };

    const { circleRsvps } = data;

    if (circleRsvps.__typename !== 'CircleRsvpCollection') return { loading: false, data: null };

    return { loading: false, data: circleRsvps };
}

export const RsvpRows: React.FC = () => {
    const { t } = useTranslation('circles');

    const comingUpCircleRsvps = useRsvpsQuery({ first: 100 });
    const previousCircleRsvps = useRsvpsQuery({ last: 100 });

    if (comingUpCircleRsvps.loading || previousCircleRsvps.loading)
        return (
            <>
                <PageSection header={t('pages.your_circles.section.coming_up.header')}>
                    <SessionCardRowSkeleton />
                </PageSection>
                <PageSection header={t('pages.your_circles.section.previous.header')}>
                    <SessionCardRowSkeleton />
                </PageSection>
            </>
        );

    const hasComingUp = !!comingUpCircleRsvps.data && comingUpCircleRsvps.data.edges.length !== 0;
    const hasPrevious = !!previousCircleRsvps.data && previousCircleRsvps.data?.edges.length !== 0;
    const isEmpty = !hasComingUp && !hasPrevious;

    // If the user has no previous or upcoming Circles, show them the
    // recommendations experience.
    if (isEmpty) return <RecommendationsRow />;

    return (
        <>
            <PageSection header={t('pages.your_circles.section.coming_up.header')}>
                {hasComingUp ? (
                    <RsvpGrid
                        circleRsvps={filterNullish(comingUpCircleRsvps.data.edges).map(
                            edge => edge.node,
                        )}
                    />
                ) : (
                    <DeprecatedInlineMessage
                        message={t(
                            'pages.your_circles.section.coming_up.conditions.no_upcoming.message',
                        )}
                    />
                )}
            </PageSection>

            <PageSection header={t('pages.your_circles.section.previous.header')}>
                {hasPrevious ? (
                    <RsvpGrid
                        circleRsvps={filterNullish(previousCircleRsvps.data.edges).map(
                            edge => edge.node,
                        )}
                        isPrevious
                    />
                ) : (
                    <DeprecatedInlineMessage
                        message={t('pages.your_circles.section.previous.conditions.empty.message')}
                        alignment="center"
                    />
                )}
            </PageSection>
        </>
    );
};

export const CirclesYourCirclesPage: React.FC<unknown> = () => {
    const { t } = useTranslation('circles');

    return (
        <Wrapper>
            <div>
                <BackButton fallbackPath="/circles">
                    {previousRoute => {
                        if (previousRoute === `/circles`) {
                            return t('routes.fallback.title');
                        }

                        return null;
                    }}
                </BackButton>
                <HeadingText50 as="h2">{t('pages.your_circles.header')}</HeadingText50>
            </div>

            <RsvpRows />
        </Wrapper>
    );
};
